import { Box, Button, CircularProgress, Typography } from '@mui/material';
import BackendService, { AdminNoteItem } from 'BackendService';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { noteAgo } from './contactOpportunitiesUtils';

const PAGE_SIZE = 20;

export default function ContactNotesSection({ contactExternalId }: { contactExternalId: string }) {
  const [notes, setNotes] = useState<AdminNoteItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bs = useCallback(() => new BackendService(), []);

  useEffect(() => {
    bs()
      .listAdminNotes('CONTACT', contactExternalId, { limit: PAGE_SIZE, offset: 0 })
      .then((res) => {
        setNotes(res.data);
        setHasMore(res.data.length === PAGE_SIZE);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [contactExternalId, bs]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const loadMore = () => {
    bs()
      .listAdminNotes('CONTACT', contactExternalId, { limit: PAGE_SIZE, offset: notes.length })
      .then((res) => {
        setNotes((prev) => [...prev, ...res.data]);
        setHasMore(res.data.length === PAGE_SIZE);
      })
      .catch(console.error);
  };

  const handleSave = () => {
    const text = draft.trim();
    if (!text) return;
    setSaveStatus('saving');
    bs()
      .createAdminNote({
        modelType: 'CONTACT',
        modelExternalId: contactExternalId,
        content: text,
      })
      .then((created) => {
        setNotes((prev) => [
          {
            id: Date.now(),
            externalId: created.data.externalId,
            adminUserId: 0,
            modelType: 'CONTACT',
            modelExternalId: contactExternalId,
            content: text,
            isVoiceNote: false,
            rawTranscript: null,
            audioGcsPath: null,
            audioDurationSeconds: null,
            dueAt: null,
            calendarLinkedAt: null,
            calendarLinkSource: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            adminDeepLink: null,
          },
          ...prev,
        ]);
        setDraft('');
        setSaveStatus('saved');
        timerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      })
      .catch(() => setSaveStatus('error'));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Box
          component="textarea"
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
          placeholder="Add a note…"
          rows={2}
          sx={{
            flex: 1,
            fontFamily: 'inherit',
            fontSize: '0.875rem',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
            resize: 'vertical',
            '&:focus': { outline: 'none', borderColor: 'primary.main' },
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={!draft.trim() || saveStatus === 'saving'}
          sx={{ alignSelf: 'flex-end', textTransform: 'none', minWidth: 72 }}
        >
          {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : 'Save'}
        </Button>
      </Box>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {!loading && notes.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No notes yet.
        </Typography>
      )}
      {notes.map((n) => (
        <Box
          key={n.id}
          sx={{
            py: 1.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.disabled">
              Admin · {noteAgo(n.createdAt)}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {n.content}
          </Typography>
        </Box>
      ))}
      {hasMore && (
        <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={loadMore}>
          Load more
        </Button>
      )}
    </Box>
  );
}
